/**
 * Modal diagnostic des matières désynchronisées dans une session EDT examens.
 *
 * Affiche un rapport clair par matière : orthographes, enseignants, créneaux
 * distincts, slot canonique proposé, blocages. Actions individuelles :
 * synchroniser cette matière (avec choix optionnel du slot cible).
 */
import React, { useState } from 'react';
import {
  X, AlertTriangle, CheckCircle2, Lock, Users, Calendar, Clock,
  Loader2, RefreshCw, Zap, ChevronDown, ChevronRight, User,
} from 'lucide-react';
import {
  useDiagnosticMatieresDesyncQuery,
  useSynchroniserMatiereUniqueMutation,
} from '../../services/emploisTempsApi';

interface Props {
  sessionId: number;
  isDark: boolean;
  onClose: () => void;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export const DiagnosticMatieresModal: React.FC<Props> = ({ sessionId, isDark, onClose }) => {
  const { data, isFetching, refetch } = useDiagnosticMatieresDesyncQuery(sessionId, {
    refetchOnMountOrArgChange: true,
  });
  const [syncMatiere, { isLoading: syncing }] = useSynchroniserMatiereUniqueMutation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);

  const toggle = (n: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  const handleSync = async (nom_normalise: string, slot_cible_id?: number) => {
    setFeedback(null);
    try {
      const r = await syncMatiere({ sessionId, nom_normalise, slot_cible_id }).unwrap();
      if (r.conflits && r.conflits.length > 0) {
        setFeedback(`${r.creneaux_deplaces} créneau(x) déplacés. ${r.conflits.length} bloqué(s).`);
      } else {
        setFeedback(`✓ ${r.creneaux_deplaces} créneau(x) alignés.`);
      }
      refetch();
    } catch (e: any) {
      setFeedback(e?.data?.error || 'Erreur');
    }
  };

  const totalDesync = data?.total_matieres_desynchronisees ?? 0;
  const totalOrtho = data?.total_multi_orthographes ?? 0;
  const totalMultiEns = data?.total_multi_enseignants ?? 0;
  const totalBloques = data?.total_bloques ?? 0;

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start sm:items-center justify-center p-3 sm:p-6"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-5xl max-h-[92vh] rounded-xl shadow-2xl flex flex-col ${
          isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
        }`}>
        <header className={`px-4 py-3 border-b flex items-center justify-between ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div>
            <h3 className="font-semibold text-sm inline-flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              Diagnostic matières désynchronisées
            </h3>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {data?.session_libelle ?? ''} · Matières présentes sur des créneaux différents entre options / pôles
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()}
              className={`p-1.5 rounded ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
              title="Rafraîchir"
              disabled={isFetching}>
              {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <X size={18} />
            </button>
          </div>
        </header>

        {/* KPIs */}
        <div className={`px-4 py-3 border-b grid grid-cols-2 md:grid-cols-4 gap-3 ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className={`p-3 rounded-lg border ${
            totalDesync > 0
              ? (isDark ? 'bg-red-950/30 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-800')
              : (isDark ? 'bg-emerald-950/30 border-emerald-800 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800')
          }`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Désynchronisées</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5">{totalDesync}</div>
          </div>
          <div className={`p-3 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-500">Multi-orthographes</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5">{totalOrtho}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Cause fréquente de désynchro</div>
          </div>
          <div className={`p-3 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-500">Multi-enseignants</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5">{totalMultiEns}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Vérifier si intentionnel</div>
          </div>
          <div className={`p-3 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-red-500">Bloquées</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5">{totalBloques}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Verrouillage à ajuster</div>
          </div>
        </div>

        {feedback && (
          <div className={`px-4 py-2 text-xs ${
            feedback.startsWith('✓')
              ? (isDark ? 'bg-emerald-950/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
              : (isDark ? 'bg-amber-950/30 text-amber-300' : 'bg-amber-50 text-amber-700')
          }`}>{feedback}</div>
        )}

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-3">
          {isFetching && !data ? (
            <div className="text-center py-16 text-sm text-gray-500">
              <Loader2 size={20} className="inline animate-spin mr-2" /> Analyse en cours…
            </div>
          ) : totalDesync === 0 ? (
            <div className={`text-center py-16 rounded-lg border ${
              isDark ? 'bg-emerald-950/20 border-emerald-800 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
            }`}>
              <CheckCircle2 size={40} className="inline mb-2 text-emerald-500" />
              <div className="font-semibold">Toutes les matières sont synchronisées</div>
              <div className="text-xs mt-1 opacity-80">
                Chaque matière commune apparaît au même créneau dans tous ses groupes / pôles.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {data!.groupes.map((g) => {
                const open = expanded.has(g.nom_normalise);
                return (
                  <div key={g.nom_normalise} className={`rounded-lg border ${
                    isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
                  }`}>
                    <button onClick={() => toggle(g.nom_normalise)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-t-lg">
                      {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{g.nom_affiche}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-semibold">
                            {g.slots_distincts} slots
                          </span>
                          {g.multi_orthographes && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                              title={g.noms_originaux.join(' · ')}>
                              {g.noms_originaux.length} orthographes
                            </span>
                          )}
                          {!g.meme_enseignant && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                              title={g.enseignants.join(' · ')}>
                              {g.enseignants.length} enseignants
                            </span>
                          )}
                          {!g.peut_etre_aligne_automatiquement && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white inline-flex items-center gap-0.5">
                              <Lock size={9} /> BLOQUÉE
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {g.enseignants.length > 0 && (
                            <span className="inline-flex items-center gap-0.5">
                              <User size={10} /> {g.enseignants.join(', ')}
                            </span>
                          )}
                          <span>· {g.creneaux.length} créneau(x) au total</span>
                        </div>
                      </div>
                      {g.peut_etre_aligne_automatiquement && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSync(g.nom_normalise); }}
                          disabled={syncing}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
                        >
                          <Zap size={11} /> Synchroniser
                        </button>
                      )}
                    </button>

                    {open && (
                      <div className={`px-4 pb-3 border-t space-y-3 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                        {/* Orthographes détectées */}
                        {g.multi_orthographes && (
                          <div className="mt-3">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">
                              Variantes d'orthographe rencontrées
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {g.noms_originaux.map(n => (
                                <span key={n} className={`text-xs px-2 py-0.5 rounded font-mono ${
                                  isDark ? 'bg-amber-950/40 text-amber-200' : 'bg-amber-50 text-amber-800'
                                }`}>{n}</span>
                              ))}
                            </div>
                            <div className="text-[10px] text-gray-500 mt-1">
                              Astuce : uniformiser les intitulés dans « Gestion Matières » évite ce type de désynchro à la prochaine génération.
                            </div>
                          </div>
                        )}

                        {/* Slot canonique proposé */}
                        <div className={`p-2 rounded ${
                          isDark ? 'bg-emerald-950/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'
                        }`}>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                            Slot proposé (le plus utilisé)
                          </div>
                          <div className="text-sm font-semibold mt-0.5 inline-flex items-center gap-2">
                            <Calendar size={12} className="text-emerald-500" />
                            {fmtDate(g.slot_canonique_propose.date)}
                            <Clock size={12} className="text-emerald-500" />
                            {g.slot_canonique_propose.heure_debut} – {g.slot_canonique_propose.heure_fin}
                            <span className="text-[10px] text-gray-500">
                              ({g.slot_canonique_propose.nb_creneaux_deja_dessus} déjà dessus)
                            </span>
                          </div>
                        </div>

                        {/* Créneaux */}
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                            Créneaux ({g.creneaux.length})
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <th className="text-left py-1 pr-2">Groupe</th>
                                  <th className="text-left py-1 pr-2">Date</th>
                                  <th className="text-left py-1 pr-2">Horaire</th>
                                  <th className="text-left py-1 pr-2">État</th>
                                  <th className="text-right py-1">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {g.creneaux.map(c => (
                                  <tr key={c.id}>
                                    <td className="py-1 pr-2 font-medium">{c.groupe_label} <span className="text-gray-400 text-[10px]">({c.niveau})</span></td>
                                    <td className="py-1 pr-2 tabular-nums">{fmtDate(c.date)}</td>
                                    <td className="py-1 pr-2 tabular-nums">{c.heure_debut} – {c.heure_fin}</td>
                                    <td className="py-1 pr-2">
                                      {c.sur_slot_canonique ? (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600">
                                          <CheckCircle2 size={10} /> Slot cible
                                        </span>
                                      ) : c.verrouille ? (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-red-600">
                                          <Lock size={10} /> Verrouillé
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-gray-500">À déplacer</span>
                                      )}
                                    </td>
                                    <td className="py-1 text-right">
                                      {!c.sur_slot_canonique && (
                                        <button
                                          onClick={() => handleSync(g.nom_normalise, c.id)}
                                          disabled={syncing}
                                          className="text-[10px] px-2 py-0.5 rounded border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                          title="Imposer CE slot à toutes les autres"
                                        >
                                          Prendre ce slot
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Blocages */}
                        {g.blocages.length > 0 && (
                          <div className={`p-2 rounded ${
                            isDark ? 'bg-red-950/20 border border-red-800' : 'bg-red-50 border border-red-200'
                          }`}>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300 mb-1">
                              Blocages détectés
                            </div>
                            <ul className="text-xs space-y-0.5">
                              {g.blocages.map((b: any, i: number) => (
                                <li key={i} className="flex items-start gap-1">
                                  <AlertTriangle size={10} className="text-red-500 flex-shrink-0 mt-0.5" />
                                  <span>
                                    <strong>{b.groupe || b.creneau_id}</strong> :{' '}
                                    {b.raison === 'verrouille'
                                      ? `créneau verrouillé sur ${b.slot}`
                                      : `conflit verrouillé avec « ${b.conflit_avec} »`}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <div className="text-[10px] text-gray-500 mt-1">
                              Déverrouillez le créneau (option ✎ dans le tableau EDT) puis relancez.
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className={`px-4 py-3 border-t text-[11px] text-gray-500 flex items-center justify-between ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <span>
            Les matières sont regroupées après normalisation du nom (accents, casse, ponctuation ignorés).
          </span>
          <button onClick={onClose}
            className={`px-3 py-1 text-xs rounded ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DiagnosticMatieresModal;
